import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../api';

const STATUS_CONFIG = {
  draft:  { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', label: 'Brouillon' },
  signed: { color: '#4ade80', bg: 'rgba(74,222,128,0.1)',  label: '✅ Signé' },
};

export default function Contrats({ clients = [] }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [shareLinks, setShareLinks] = useState({});
  const fileRef = useRef();

  const [form, setForm] = useState({ title: '', description: '', client_id: '', file: null });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.getContracts();
      setContracts(r.contracts || []);
    } catch (e) {
      toast.error(e.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    setUploading(true);
    try {
      let contract;
      if (form.file) {
        const r = await api.uploadContract(form.file, null, form.title, form.client_id || null);
        if (r.error) throw new Error(r.error);
        contract = r.contract;
        // Update description if provided
        if (form.description && contract) {
          // We'll just show it — description update via future PUT
        }
      } else {
        const r = await api.createContract({ title: form.title, description: form.description, client_id: form.client_id || null });
        contract = r.contract;
      }
      toast.success('Contrat créé !');
      setShowModal(false);
      setForm({ title: '', description: '', client_id: '', file: null });
      load();
    } catch (e) {
      toast.error(e.message || 'Erreur création');
    } finally {
      setUploading(false);
    }
  };

  const handleShare = async (id) => {
    try {
      const r = await api.shareContract(id);
      setShareLinks(prev => ({ ...prev, [id]: r.url }));
      navigator.clipboard.writeText(r.url).catch(() => {});
      toast.success('Lien de signature copié !');
    } catch (e) {
      toast.error(e.message || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce contrat ?')) return;
    try {
      await api.deleteContract(id);
      toast.success('Contrat supprimé');
      load();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    }
  };

  const s = {
    card: { background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1rem' },
    btn: { background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' },
    btnSm: (c = '#3b82f6', bg = 'rgba(59,130,246,0.12)') => ({ background: bg, color: c, border: `1px solid ${c}33`, borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }),
    input: { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.6rem 0.75rem', color: '#e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' },
    label: { color: '#94a3b8', fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.4rem' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  };

  return (
    <div style={{ color: '#e2e8f0', maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'white', margin: 0 }}>📄 Contrats & Documents</h2>
          <p style={{ color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>Envoyez des documents à signer électroniquement</p>
        </div>
        <button style={s.btn} onClick={() => setShowModal(true)}>+ Nouveau contrat</button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: '#64748b', padding: '3rem', textAlign: 'center' }}>⏳ Chargement...</div>
      ) : contracts.length === 0 ? (
        <div style={{ ...s.card, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
          <div style={{ color: '#64748b', fontSize: '1rem' }}>Aucun contrat pour l'instant</div>
          <div style={{ color: '#475569', fontSize: '0.85rem', marginTop: '0.5rem' }}>Créez un contrat et envoyez le lien de signature à votre client</div>
          <button style={{ ...s.btn, marginTop: '1.5rem' }} onClick={() => setShowModal(true)}>+ Créer mon premier contrat</button>
        </div>
      ) : (
        <div>
          {contracts.map(c => {
            const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
            const signedDate = c.signed_at ? new Date(c.signed_at).toLocaleDateString('fr-FR') : null;
            const shareUrl = shareLinks[c.id];
            return (
              <div key={c.id} style={{ ...s.card, display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                {/* Icon */}
                <div style={{ fontSize: '2rem', flexShrink: 0, marginTop: '0.1rem' }}>
                  {c.status === 'signed' ? '✅' : c.file_url ? '📎' : '📄'}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', color: 'white', fontSize: '1rem' }}>{c.title}</span>
                    <span style={{ background: cfg.bg, color: cfg.color, borderRadius: '1rem', padding: '0.15rem 0.6rem', fontSize: '0.75rem', fontWeight: '600' }}>{cfg.label}</span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.25rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    {c.client_name && <span>👤 {c.client_name}</span>}
                    {c.file_name && <span>📎 {c.file_name}</span>}
                    {c.signed_at && <span>✍️ Signé le {signedDate} par {c.signer_name}</span>}
                    {!c.signed_at && <span>📅 Créé le {new Date(c.created_at).toLocaleDateString('fr-FR')}</span>}
                  </div>
                  {shareUrl && (
                    <div style={{ marginTop: '0.5rem', background: 'rgba(59,130,246,0.1)', borderRadius: '0.375rem', padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: '#60a5fa', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      🔗 {shareUrl}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {c.status !== 'signed' && (
                    <button style={s.btnSm('#a78bfa', 'rgba(167,139,250,0.1)')} onClick={() => handleShare(c.id)}>
                      🔗 {shareLinks[c.id] ? 'Copié !' : 'Lien signature'}
                    </button>
                  )}
                  {c.file_url && (
                    <a
                      href={`https://api.akodigitale.fr${c.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...s.btnSm('#38bdf8', 'rgba(56,189,248,0.1)'), textDecoration: 'none' }}
                    >
                      ⬇ Fichier
                    </a>
                  )}
                  <button style={s.btnSm('#f87171', 'rgba(248,113,113,0.1)')} onClick={() => handleDelete(c.id)}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Création */}
      {showModal && (
        <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '1rem', padding: '2rem', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              📄 Nouveau contrat
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={s.label}>Titre du contrat *</label>
              <input
                style={s.input}
                placeholder="Ex : Contrat de prestation — Charpente 2026"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={s.label}>Description (optionnel)</label>
              <textarea
                style={{ ...s.input, minHeight: '80px', resize: 'vertical' }}
                placeholder="Détails du contrat..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            {clients.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={s.label}>Client (optionnel)</label>
                <select
                  style={{ ...s.input, cursor: 'pointer' }}
                  value={form.client_id}
                  onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                >
                  <option value="">— Aucun client —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {/* File upload */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={s.label}>Fichier PDF (optionnel)</label>
              <div
                onClick={() => fileRef.current?.click()}
                style={{ border: '2px dashed #334155', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center', cursor: 'pointer', background: form.file ? 'rgba(59,130,246,0.08)' : 'transparent', transition: 'all 0.2s' }}
              >
                {form.file ? (
                  <div style={{ color: '#60a5fa' }}>📎 {form.file.name}</div>
                ) : (
                  <div style={{ color: '#64748b' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>☁️</div>
                    <div style={{ fontSize: '0.85rem' }}>Cliquez pour uploader un PDF ou Word</div>
                    <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: '#475569' }}>Max 20 Mo</div>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && setForm(f => ({ ...f, file: e.target.files[0] }))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                style={{ ...s.btn, flex: 1, opacity: uploading ? 0.7 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}
                onClick={handleCreate}
                disabled={uploading}
              >
                {uploading ? '⏳ Envoi...' : '✅ Créer le contrat'}
              </button>
              <button
                style={{ background: 'rgba(148,163,184,0.1)', color: '#94a3b8', border: '1px solid #334155', borderRadius: '0.5rem', padding: '0.6rem 1rem', cursor: 'pointer' }}
                onClick={() => { setShowModal(false); setForm({ title: '', description: '', client_id: '', file: null }); }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
